import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REJECTION_REASONS, type PublicQuote, type RejectionReason } from '@worki/shared';
import { apiHeaders, RequestError } from '../lib/api.js';
import { apiUrl } from '../lib/config.js';
import { Button, Card, ErrorNotice, Field, LoadingScreen, Textarea } from '../components/ui.js';
import { Modal } from '../components/Modal.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { QuoteDocument, publicQuoteToDoc } from '../components/QuoteDocument.js';

/**
 * QUO-009 / QUO-013..017 — the customer-facing quote page.
 *
 * Unauthenticated by design: anyone with the link sees the quote, approves with
 * the 4-digit code, or rejects with a reason. Talks to `/api/public/*` only.
 *
 * 2.11 — the quote itself is `QuoteDocument`, the same component every owner
 * screen renders. This page used to lay the document out again in its own
 * markup, and the two slid apart: the owner previewed a cover band, a round
 * logo, the business phone, a labelled subject and the business's own sign-off
 * against a customer page that showed none of them. What is left here is the
 * chrome only a customer needs — the language switch, the outcome panels, and
 * the approve / reject row. The download is in the approved panel, and only
 * there (2.4). Nothing the owner can do to a quote belongs on it.
 */

async function publicRequest<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(`/public${path}`), {
      ...init,
      // Public-link requests still need the browser CSRF header when a session
      // cookie exists, but must not attach a native session Bearer credential.
      headers: await apiHeaders({ 'Content-Type': 'application/json', ...(init?.headers ?? {}) }, true),
    });
  } catch {
    throw new RequestError(0, 'errors.network');
  }

  const payload: unknown = response.headers.get('content-type')?.includes('application/json')
    ? await response.json()
    : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string } } | null)?.error;
    throw new RequestError(response.status, error?.code ?? 'errors.unknown');
  }
  return payload as T;
}

export function PublicQuotePage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [blockedToken, setBlockedToken] = useState<string | undefined>();

  const quoteQuery = useQuery({
    queryKey: ['public-quote', token],
    queryFn: () => publicRequest<PublicQuote>(`/quotes/${token}`),
    retry: false,
  });

  const quote = quoteQuery.data;
  const decided = quote?.status === 'approved' || quote?.status === 'rejected';

  // A heartbeat also checks access after locking/deletion. Keep checking
  // decided documents without charging hidden-tab time as engagement.
  useEffect(() => {
    if (!quote || blockedToken === token) return undefined;
    let active = true;
    let visible = document.visibilityState === 'visible';
    let last = Date.now();
    let viewedSeconds = 0;
    const accrue = () => {
      const now = Date.now();
      if (visible && !decided && !quote.expired) viewedSeconds += (now - last) / 1000;
      last = now;
    };
    const checkAccess = () => {
      accrue();
      const seconds = Math.floor(viewedSeconds);
      viewedSeconds -= seconds;
      void apiHeaders({ 'Content-Type': 'application/json' }, true).then((headers) => fetch(apiUrl(`/public/quotes/${token}/heartbeat`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ seconds }),
      })).then((response) => {
        if (active && response.status === 404) {
          setBlockedToken(token);
          setApproveOpen(false);
          setRejectOpen(false);
        }
      }).catch(() => undefined);
    };
    const onVisibility = () => {
      accrue();
      visible = document.visibilityState === 'visible';
      checkAccess();
    };
    const tick = setInterval(checkAccess, 15_000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [quote, decided, token, blockedToken]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['public-quote', token] });

  if (quoteQuery.isLoading) return <LoadingScreen />;

  if (quoteQuery.isError || !quote || blockedToken === token) {
    return (
      <div className="public-page">
        <div className="public-shell">
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                🔎
              </span>
              <h1>{t('publicQuote.notFound')}</h1>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  /*
   * QUO-009 — clear expired state, never an error page. The document itself
   * stays closed: the link is what expired, and the prices in it are not for
   * reading indefinitely. Whose quote it was, and which one, is what the
   * reader needs to go back and ask for another.
   */
  if (quote.expired && !decided) {
    return (
      <div className="public-page">
        <div className="public-shell">
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                ⏳
              </span>
              <h1>{t('publicQuote.expiredTitle')}</h1>
              <p className="muted">{t('publicQuote.expiredBody', { count: quote.validDays })}</p>
              <p className="subtle">
                {t('publicQuote.from', { business: quote.business.name })}
                {' · '}
                {t('quotes.numberShort', { number: quote.number })}
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="public-shell">
        <div className="row row--end">
          <LanguageSwitcher />
        </div>

        {quote.status === 'sent' && quote.pdfUrl && !quote.expired && <div className="mb-3 flex justify-end"><a href={quote.pdfUrl} target="_blank" rel="noreferrer"><Button variant="primary">📄 {t('quotes.downloadPdf')}</Button></a></div>}
        {quote.status === 'approved' && (
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                ✅
              </span>
              <h1>{t('publicQuote.approved')}</h1>
              <p className="muted">{t('publicQuote.approvedBody')}</p>
              {/* 2.4 — the file is the copy of what was agreed, so it is
                  offered here, with the approval, as the signed page offers
                  its copy. It used to sit beside approve and reject, which
                  handed the quote over as a file before anyone had agreed. */}
              {quote.pdfUrl && (
                <a href={quote.pdfUrl} target="_blank" rel="noreferrer">
                  <Button variant="primary">📄 {t('quotes.downloadPdf')}</Button>
                </a>
              )}
            </div>
          </Card>
        )}

        {quote.status === 'rejected' && (
          <Card>
            <div className="result-panel">
              <span className="result-panel__icon" aria-hidden>
                🙏
              </span>
              <h1>{t('publicQuote.rejected')}</h1>
              <p className="muted">{t('publicQuote.rejectedBody')}</p>
            </div>
          </Card>
        )}

        {/* The quote, as the business previewed it before sending — masthead,
            "to", items, albums, notes, terms, totals and their own sign-off. */}
        <Card padded={false}>
          <QuoteDocument doc={publicQuoteToDoc(quote)} />
        </Card>

        {!decided && (
          <Card>
            <div className="row row--wrap" style={{ justifyContent: 'center', gap: 'var(--space-3)' }}>
              <Button variant="success" size="lg" onClick={() => setApproveOpen(true)}>
                ✓ {t('publicQuote.approve')}
              </Button>
              <Button variant="secondary" size="lg" onClick={() => setRejectOpen(true)}>
                ✕ {t('publicQuote.reject')}
              </Button>
            </div>
          </Card>
        )}

        {/* "Produced with Worki" is printed at the foot of the document
            itself, under the business's own sign-off, exactly as it is on the
            PDF — so the page no longer says it a second time. */}

        <ApproveDialog
          open={approveOpen}
          token={token!}
          contentRevision={quote.contentRevision}
          onReload={() => { setApproveOpen(false); void quoteQuery.refetch(); }}
          onClose={() => setApproveOpen(false)}
          onApproved={refresh}
        />
        <RejectDialog
          open={rejectOpen}
          token={token!}
          onClose={() => setRejectOpen(false)}
          onRejected={refresh}
        />
      </div>
    </div>
  );
}

/**
 * QUO-013/014 — two-step approval:
 * step 1 requests a fresh code and displays it; step 2 verifies what the
 * customer types. Wrong code keeps the dialog open for a retry.
 */
function ApproveDialog({
  open,
  token,
  contentRevision,
  onReload,
  onClose,
  onApproved,
}: {
  open: boolean;
  token: string;
  contentRevision: number;
  onReload: () => void;
  onClose: () => void;
  onApproved: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string | null>(null);
  const [entered, setEntered] = useState('');
  const [error, setError] = useState<string | null>(null);
  const approvalRevision = useRef(contentRevision);

  const start = useMutation({
    mutationFn: () => publicRequest<{ code: string }>(`/quotes/${token}/approve/start`, {
      method: 'POST', body: JSON.stringify({ expectedRevision: approvalRevision.current }),
    }),
    onSuccess: (data) => {
      setCode(data.code);
      setError(null);
    },
    onError: (err) => setError(err instanceof RequestError ? err.code : 'errors.unknown'),
  });

  const verify = useMutation({
    mutationFn: () =>
      publicRequest<PublicQuote>(`/quotes/${token}/approve`, {
        method: 'POST',
        body: JSON.stringify({ code: entered, expectedRevision: approvalRevision.current }),
      }),
    onSuccess: () => {
      onApproved();
      onClose();
    },
    onError: (err) => setError(err instanceof RequestError ? err.code : 'errors.unknown'),
  });

  useEffect(() => {
    if (open) {
      approvalRevision.current = contentRevision;
      setCode(null);
      setEntered('');
      setError(null);
      start.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      title={t('publicQuote.approveTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {error === 'errors.conflict' ? <Button onClick={onReload}>{t('publicQuote.reloadLatest')}</Button> : <Button
            variant="success"
            disabled={!code || entered.length !== 4}
            loading={verify.isPending}
            onClick={() => verify.mutate()}
          >
            {t('publicQuote.confirmApprove')}
          </Button>}
        </>
      }
    >
      <div className="stack">
        {error === 'errors.conflict' ? <p role="alert">{t('publicQuote.revisionChanged')}</p> : error && <ErrorNotice code={error} />}

        {code && (
          <>
            <p>{t('publicQuote.approveIntro')}</p>
            <div className="code-display" aria-label={t('publicQuote.yourCode')}>
              {[...code].map((digit, index) => (
                <span key={index} className="code-digit">
                  {digit}
                </span>
              ))}
            </div>
            <p className="muted">{t('publicQuote.codeHint')}</p>
          </>
        )}

        <Field label={t('publicQuote.enterCode')} htmlFor="approval-code">
          <input
            id="approval-code"
            className="code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            value={entered}
            onChange={(event) => setEntered(event.target.value.replace(/\D/g, '').slice(0, 4))}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** QUO-016 — rejection requires one of the four fixed reasons. */
function RejectDialog({
  open,
  token,
  onClose,
  onRejected,
}: {
  open: boolean;
  token: string;
  onClose: () => void;
  onRejected: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<RejectionReason | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reject = useMutation({
    mutationFn: () =>
      publicRequest<PublicQuote>(`/quotes/${token}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason, note: note.trim() || undefined }),
      }),
    onSuccess: () => {
      onRejected();
      onClose();
    },
    onError: (err) => setError(err instanceof RequestError ? err.code : 'errors.unknown'),
  });

  useEffect(() => {
    if (open) {
      setReason(null);
      setNote('');
      setError(null);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      title={t('publicQuote.rejectTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" disabled={!reason} loading={reject.isPending} onClick={() => reject.mutate()}>
            {t('publicQuote.confirmReject')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {error && <ErrorNotice code={error} />}
        <p>{t('publicQuote.rejectIntro')}</p>

        <div className="stack stack--tight" role="radiogroup" aria-label={t('quotes.rejectionReason')}>
          {REJECTION_REASONS.map((option) => (
            <label
              key={option}
              className="list-row"
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                background: reason === option ? 'var(--color-primary-soft)' : 'transparent',
              }}
            >
              <input
                type="radio"
                name="rejection-reason"
                className="checkbox"
                checked={reason === option}
                onChange={() => setReason(option)}
              />
              {t(`enum.rejectionReason.${option}`)}
            </label>
          ))}
        </div>

        <Field label={t('publicQuote.rejectNote')} hint={t('common.optional')}>
          <Textarea value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
